// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../components/common/Loader';
import { clearAuthState } from '../services/authStorage';
import { resolvePostLoginRoute } from '../orbit/lib/postLoginRoute';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * One Identity P2.3 — the invite accept page (/invite/:token).
 *
 * Public: the single-use token in the URL is the authorization. New people
 * choose their own password here (credentials never travel by email);
 * existing people just accept and are sent to login.
 */
const InviteAccept = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [invite, setInvite] = useState(null);
    const [error, setError] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/invite/${token}`);
                const data = await res.json();
                if (!res.ok) throw new Error(data.detail || 'Invite not found');
                setInvite(data);
                // Prefilled from what the inviter entered; the person can change it.
                if (data.name) setName(data.name);
            } catch (e) {
                setError(e.message || 'This invite link is invalid, used, or expired');
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const handleAccept = async (e) => {
        e.preventDefault();
        if (invite.needs_password) {
            if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
            if (password !== confirm) { toast.error('Passwords do not match'); return; }
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/invite/${token}/accept`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(invite.needs_password ? { password, name, phone: phone.trim() || undefined } : {}),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || 'Could not accept the invite');

            // Redeeming the link signs you in — the same keys a password login
            // writes, so the app cannot tell the two apart. No token (session
            // issue failed server-side) falls back to the login wall.
            if (!data.token || !data.user) {
                toast.success(`You've joined ${data.workspace_name}. Sign in to continue.`);
                navigate('/login');
                return;
            }
            clearAuthState();
            localStorage.setItem('auth_token', data.token);
            if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
            localStorage.setItem('user_role', data.user.role);
            localStorage.setItem('user_id', data.user.id);
            localStorage.setItem('is_provider', data.user.is_provider ? '1' : '');
            localStorage.setItem('is_builder_admin', data.user.is_builder_admin ? '1' : '');
            localStorage.setItem('is_expert', data.user.is_expert ? '1' : '');
            if (data.user.client_id) localStorage.setItem('client_id', data.user.client_id);
            if (data.user.email) localStorage.setItem('user_email', data.user.email);
            if (data.user.name) localStorage.setItem('user_name', data.user.name);

            toast.success(`Welcome to ${data.workspace_name}`);
            // An invited member starts on the setup, as the Owner does. A
            // trainer (consultant) or an expert goes to their own home instead.
            let target = '/setup';
            if (data.user.role !== 'client' && data.user.role !== 'client_admin') {
                target = await resolvePostLoginRoute(
                    data.user.role,
                    data.user.client_id ?? null,
                    false,
                );
            }
            // Full reload, not navigate() — AuthContext and the role hooks read
            // localStorage on mount, so an in-router push would keep stale state.
            window.location.href = target;
        } catch (e) {
            toast.error(e.message || 'Could not accept the invite');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="flex justify-center pt-24"><Loader /></div>;

    if (error) {
        return (
            <div className="max-w-md mx-auto mt-16 bg-white rounded-xl border border-gray-200 p-8 text-center">
                <h1 className="text-lg font-semibold text-gray-900 mb-2">Invite unavailable</h1>
                <p className="text-sm text-gray-600 mb-6">{error}</p>
                <p className="text-sm text-gray-500">
                    Ask the person who invited you to send a fresh link, or{' '}
                    <button onClick={() => navigate('/login')} className="text-brand-500 font-medium">sign in</button>{' '}
                    if you already have an account.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto mt-16 bg-white rounded-xl border border-gray-200 p-8">
            <h1 className="text-lg font-semibold text-gray-900 mb-1">
                Join {invite.workspace_name}
            </h1>
            <p className="text-sm text-gray-600 mb-6">
                {invite.invited_by ? `${invite.invited_by} invited` : 'You were invited as'}{' '}
                <span className="font-medium">{invite.email}</span>
                {invite.role ? ` (${invite.role.replaceAll('_', ' ').toLowerCase()})` : ''}.
            </p>

            <form onSubmit={handleAccept} className="space-y-4">
                {invite.needs_password && (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Your name</label>
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                placeholder="How teammates will see you" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone number <span className="font-normal text-gray-400">(optional)</span></label>
                            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                placeholder="+1 555 0100" autoComplete="tel" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Choose a password</label>
                            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                placeholder="At least 8 characters" autoComplete="new-password" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
                            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                autoComplete="new-password" required />
                        </div>
                    </>
                )}
                {!invite.needs_password && (
                    <p className="text-sm text-gray-500">
                        You already have an account — accept below, then sign in as usual.
                    </p>
                )}
                <button type="submit" disabled={submitting}
                    className="w-full bg-brand-500 text-white rounded-lg py-2.5 text-sm font-semibold disabled:opacity-60">
                    {submitting ? 'Joining…' : `Accept invite`}
                </button>
            </form>
        </div>
    );
};

export default InviteAccept;
